import { Modal, ModalBody, Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IconTrash2 from "@/icons/IconTrash2";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import IconCheckMark from "@/icons/IconCheckMark";
import { IconClose } from "@velaro/velaro-component-lib";
import { AlertTriangle } from "@velaro/velaro-component-lib";
import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { PrimaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { useNavigate } from "react-router-dom";
import useToastAlert from "@/hooks/useToastAlert";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  consumerKey: string;
  consumerSecret: string;
  token: string;
  tokenSecret: string;
  restletUrl: string;
  teamId: number;
}

interface Props {
  configs: NetSuiteConfig[];
  deleteConfig(id: number): void;
  addConfig(): void;
  teamId: number;
  onChange(id: number, field: keyof NetSuiteConfig, value: string): void;
  onSelectConfig(id: number): void;
  onCreateMode?: boolean;
}

export default function NetSuiteAccounts(props: Props) {
  const [showSetupModal, setShowSetupModal] = useState(false);
  const api = useApi();
  const [company, setCompany] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [token, setToken] = useState("");
  const [tokenSecret, setTokenSecret] = useState("");
  const [restletUrl, setRestletUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();
  const [selectedConfigId, setSelectedConfigId] = useState(
    props.configs.length === 1 ? props.configs[0].id : 0
  );
  const [selectedConfig, setSelectedConfig] = useState<NetSuiteConfig | null>(
    props.configs.length > 0 ? props.configs[0] : null
  );

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    displayName: string;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);
  const consumerKeyInputRef = useRef<HTMLInputElement>(null);
  const consumerSecretInputRef = useRef<HTMLInputElement>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const tokenSecretInputRef = useRef<HTMLInputElement>(null);
  const restletUrlInputRef = useRef<HTMLInputElement>(null);
  const [showConsumerKey, setShowConsumerKey] = useState(false);
  const [showConsumerSecret, setShowConsumerSecret] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showTokenSecret, setShowTokenSecret] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [consumerKeyFocused, setConsumerKeyFocused] = useState(false);
  const [consumerSecretFocused, setConsumerSecretFocused] = useState(false);
  const [tokenFocused, setTokenFocused] = useState(false);
  const [tokenSecretFocused, setTokenSecretFocused] = useState(false);
  const [displayNameFocused, setDisplayNameFocused] = useState(false);
  const [restletUrlFocused, setRestletUrlFocused] = useState(false);
  const toast = useToastAlert();
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    consumerKey: "",
    consumerSecret: "",
    token: "",
    tokenSecret: "",
    restletUrl: ""
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (props.configs.length > 0) {
      if (selectedConfigId) {
        const existingConfig = props.configs.find(
          (c) => c.id === selectedConfigId
        );
        if (existingConfig) {
          setSelectedConfig(existingConfig);
          return;
        }
      }

      const lastConfig = props.configs[props.configs.length - 1];
      setSelectedConfigId(lastConfig.id);
      setSelectedConfig(lastConfig);
      props.onSelectConfig(lastConfig.id);
    } else {
      setSelectedConfig(null);
    }
  }, [props.configs]);

  const handleSelect = (config: NetSuiteConfig) => {
    if (editingId !== config.id) {
      setSelectedConfig(config);
      setSelectedConfigId(config.id);
      props.onSelectConfig(config.id);
      setIsOpen(false);
    }
  };

  const handleDeleteClick = (id: number, displayName: string) => {
    setConfigToDelete({ id, displayName });
    setIsModalOpen(true);
  };

  const confirmDelete = async (id?: number) => {
    if (!id) return;
    await api.messaging.delete(`NetSuite/Integrations/${id}`);
    props.deleteConfig(id);
    setIsModalOpen(false);
    setConfigToDelete(null);
  };

  const maskValue = (value: string) => {
    if (!value) return "";
    return "•".repeat(value.length);
  };

  const partiallyMaskValue = (value: string) => {
    if (!value) return "";
    if (value.length <= 8) return "•".repeat(value.length);
    const firstChars = value.slice(0, 4);
    const lastChars = value.slice(-4);
    return `${firstChars}${"•".repeat(Math.max(0, value.length - 8))}${lastChars}`;
  };

  const handleEditClick = () => {
    if (selectedConfig) {
      setEditFormData({
        displayName: selectedConfig.displayName,
        consumerKey: "", // User must re-enter
        consumerSecret: "", // User must re-enter
        token: "", // User must re-enter
        tokenSecret: "", // User must re-enter
        restletUrl: selectedConfig.restletUrl
      });
      setIsEditing(true);
    }
  };


  const handleSaveEdit = async () => {
    if (!selectedConfig) return;

    if (!editFormData.token || !editFormData.tokenSecret) {
      toast.displayToast(
        Severity.Error,
        "Please enter both token and token secret to update credentials"
      );
      return;
    }

    setSending(true);
    try {
      const response = await api.messaging.post(
        `NetSuite/Auth/UpdateCredentials/${selectedConfig.id}`,
        {
          displayName: editFormData.displayName,
          company: selectedConfig.company,
          consumerKey: editFormData.consumerKey || undefined,
          consumerSecret: editFormData.consumerSecret || undefined,
          token: editFormData.token,
          tokenSecret: editFormData.tokenSecret,
          restletUrl: editFormData.restletUrl
        }
      );

      if (response.ok) {
        props.onChange(
          selectedConfig.id,
          "displayName",
          editFormData.displayName
        );
        props.onChange(selectedConfig.id, "token", editFormData.token);
        props.onChange(
          selectedConfig.id,
          "tokenSecret",
          editFormData.tokenSecret
        );
        props.onChange(
          selectedConfig.id,
          "restletUrl",
          editFormData.restletUrl
        );
        setIsEditing(false);

        const timer = setTimeout(() => {
          toast.displayToast(Severity.Success, "Account updated successfully! Audit log entry created.");
          clearTimeout(timer);
        }, 3000);
      } else {
        toast.displayToast(
          Severity.Error,
          "Failed to update credentials. Please verify the tokens are correct."
        );
      }
    } catch (error) {
      console.error("Failed to update credentials", error);
      toast.displayToast(
        Severity.Error,
        "Failed to update credentials. Please try again."
      );
    }
    setSending(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      displayName: "",
      consumerKey: "",
      consumerSecret: "",
      token: "",
      tokenSecret: "",
      restletUrl: ""
    });
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isEditing) return;

    const input = e.target;

    if (input === displayNameRef.current) {
      setDisplayNameFocused(true);
    } else if (input === consumerKeyInputRef.current) {
      setConsumerKeyFocused(true);
    } else if (input === consumerSecretInputRef.current) {
      setConsumerSecretFocused(true);
    } else if (input === tokenInputRef.current) {
      setTokenFocused(true);
    } else if (input === tokenSecretInputRef.current) {
      setTokenSecretFocused(true);
    } else if (input === restletUrlInputRef.current) {
      setRestletUrlFocused(true);
    }

    setTimeout(() => {
      let length = 0;

      if (input === displayNameRef.current) {
        length = editFormData.displayName.length;
      } else if (input === consumerKeyInputRef.current) {
        length = editFormData.consumerKey.length;
      } else if (input === consumerSecretInputRef.current) {
        length = editFormData.consumerSecret.length;
      } else if (input === tokenInputRef.current) {
        length = editFormData.token.length;
      } else if (input === tokenSecretInputRef.current) {
        length = editFormData.tokenSecret.length;
      } else if (input === restletUrlInputRef.current) {
        length = editFormData.restletUrl.length;
      }

      input.setSelectionRange(length, length);
    }, 0);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;

    if (input === displayNameRef.current) {
      setDisplayNameFocused(false);
    } else if (input === consumerKeyInputRef.current) {
      setConsumerKeyFocused(false);
    } else if (input === consumerSecretInputRef.current) {
      setConsumerSecretFocused(false);
    } else if (input === tokenInputRef.current) {
      setTokenFocused(false);
    } else if (input === tokenSecretInputRef.current) {
      setTokenSecretFocused(false);
    } else if (input === restletUrlInputRef.current) {
      setRestletUrlFocused(false);
    }
  };

  const handleToggleTokenVisibility = () => {
    setShowToken(!showToken);
    if (!showToken && tokenInputRef.current) {
      setTimeout(() => {
        tokenInputRef.current?.focus();
        const length = isEditing ? editFormData.token.length : (selectedConfig?.token || "").length;
        tokenInputRef.current?.setSelectionRange(length, length);
      }, 0);
    }
  };

  const handleToggleTokenSecretVisibility = () => {
    setShowTokenSecret(!showTokenSecret);
    if (!showTokenSecret && tokenSecretInputRef.current) {
      setTimeout(() => {
        tokenSecretInputRef.current?.focus();
        const length = isEditing ? editFormData.tokenSecret.length : (selectedConfig?.tokenSecret || "").length;
        tokenSecretInputRef.current?.setSelectionRange(length, length);
      }, 0);
    }
  };

  async function handleSubmit() {
    setSending(true);
    const response = await api.messaging.post("NetSuite/Auth/SaveCredentials", {
      tenantId: tenantId,
      displayName: displayName,
      company: company,
      token: token,
      tokenSecret: tokenSecret,
      restletUrl: restletUrl,
      teamId: props.teamId
    });
    setSuccess(response.ok);
    if (response.ok) {
      props.addConfig();
      setCompany("");
      setDisplayName("");
      setToken("");
      setTokenSecret("");
      setRestletUrl("");
      setShowSetupModal(false);
    }
    setSending(false);
  }

  // ─── Credential field helper ───────────────────────────────────────────────
  function CredentialField({
    label,
    hint,
    inputRef,
    viewValue,
    editValue,
    editPlaceholder,
    showReveal,
    revealed,
    onReveal,
    onFocus,
    onBlur,
    onChange,
    isFocused,
    disabled = false,
  }: {
    label: string;
    hint?: string;
    inputRef?: React.RefObject<HTMLInputElement>;
    viewValue: string;
    editValue: string;
    editPlaceholder?: string;
    showReveal?: boolean;
    revealed?: boolean;
    onReveal?: () => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (v: string) => void;
    isFocused?: boolean;
    disabled?: boolean;
  }) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-600">{label}</label>
        {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={isEditing ? editValue : viewValue}
            onChange={(e) => onChange?.(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={isEditing ? (editPlaceholder ?? "") : ""}
            disabled={!isEditing || disabled}
            className={`w-full px-3 py-2 pr-10 border rounded-lg text-slate-800 text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-slate-50 ${isFocused && isEditing ? "border-blue-500 ring-1 ring-blue-500" : ""}`}
          />
          {isEditing && !disabled ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <i className="fa-solid fa-pen text-xs" />
            </span>
          ) : showReveal && !isEditing ? (
            <button
              type="button"
              onClick={onReveal}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <i className={`fa-solid ${revealed ? "fa-eye-slash" : "fa-eye"}`} />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`${props.onCreateMode ? "" : "panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6"}`}>
      {/* Header */}
      {!props.onCreateMode && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Connected Account</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              OAuth credentials for NetSuite token-based auth. Stored encrypted.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <>
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
                  title="Edit credentials"
                >
                  <IconEditPencil2 className="w-4" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => selectedConfig && handleDeleteClick(selectedConfig.id, selectedConfig.displayName)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50 transition"
                  title="Remove account"
                >
                  <IconTrash2 className="w-4" /> Remove
                </button>
              </>
            )}
            {isEditing && (
              <>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={sending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  <IconCheckMark className="w-4" /> Save
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={sending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
                >
                  <IconClose className="w-4" /> Cancel
                </button>
              </>
            )}
            <PrimaryBlueButton
              label="Add Account"
              onClick={() => navigate(`/integrations/netsuite/netsuitecreate`, { state: { teamId: props.teamId } })}
              prefixIcon={React.createElement(IconPlus)}
              className="h-8 text-xs font-medium"
            />
          </div>
        </div>
      )}

      {/* Account selector (multiple configs) */}
      {props.configs.length > 1 && !isEditing && (
        <div ref={dropdownRef} className="relative mb-4 w-64">
          <button
            className="w-full text-sm font-normal bg-white border border-gray-300 rounded-lg px-4 py-2 text-left focus:ring-1 focus:ring-blue-500 flex items-center justify-between"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span>{selectedConfig?.displayName || "Select account"}</span>
            <i className={`fa-solid ${isOpen ? "fa-angle-up" : "fa-angle-down"} text-slate-400`} />
          </button>
          {isOpen && (
            <ul className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2">
              {props.configs.map((config) => (
                <li
                  key={config.id}
                  className={`flex justify-between items-center rounded-lg px-3 py-2.5 hover:bg-slate-100 text-sm cursor-pointer ${config.id === selectedConfigId ? "text-blue-600 bg-slate-100" : "text-slate-800"}`}
                  onClick={() => handleSelect(config)}
                >
                  {config.displayName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Credential card */}
      {selectedConfig && (
        <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
          {/* Config ID + Status row */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-mono text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded">ID: {selectedConfig.id}</span>
            <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
              <svg viewBox="0 0 8 8" width="7" height="7" fill="none"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Connected
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CredentialField
              label="Display Name"
              inputRef={displayNameRef}
              viewValue={selectedConfig.displayName}
              editValue={editFormData.displayName}
              editPlaceholder="e.g. Velaro Production"
              onChange={(v) => setEditFormData({ ...editFormData, displayName: v })}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={displayNameFocused}
            />
            <CredentialField
              label="NetSuite Account ID"
              viewValue={selectedConfig.company}
              editValue={selectedConfig.company}
              disabled
              hint="Read-only — set when the account was created"
            />
            <CredentialField
              label="Consumer Key"
              hint="From NetSuite → Setup → Integration Record"
              inputRef={consumerKeyInputRef}
              viewValue={showConsumerKey ? partiallyMaskValue(selectedConfig.consumerKey) : maskValue(selectedConfig.consumerKey)}
              editValue={editFormData.consumerKey}
              editPlaceholder="Enter consumer key"
              showReveal
              revealed={showConsumerKey}
              onReveal={() => setShowConsumerKey(!showConsumerKey)}
              onChange={(v) => setEditFormData({ ...editFormData, consumerKey: v })}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={consumerKeyFocused}
            />
            <CredentialField
              label="Consumer Secret"
              hint="From NetSuite → Setup → Integration Record"
              inputRef={consumerSecretInputRef}
              viewValue={showConsumerSecret ? partiallyMaskValue(selectedConfig.consumerSecret) : maskValue(selectedConfig.consumerSecret)}
              editValue={editFormData.consumerSecret}
              editPlaceholder="Enter consumer secret"
              showReveal
              revealed={showConsumerSecret}
              onReveal={() => setShowConsumerSecret(!showConsumerSecret)}
              onChange={(v) => setEditFormData({ ...editFormData, consumerSecret: v })}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={consumerSecretFocused}
            />
            <CredentialField
              label="Token"
              hint="From NetSuite → Setup → Access Tokens"
              inputRef={tokenInputRef}
              viewValue={showToken ? partiallyMaskValue(selectedConfig.token) : maskValue(selectedConfig.token)}
              editValue={editFormData.token}
              editPlaceholder="Enter token"
              showReveal
              revealed={showToken}
              onReveal={handleToggleTokenVisibility}
              onChange={(v) => setEditFormData({ ...editFormData, token: v })}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={tokenFocused}
            />
            <CredentialField
              label="Token Secret"
              hint="From NetSuite → Setup → Access Tokens"
              inputRef={tokenSecretInputRef}
              viewValue={showTokenSecret ? partiallyMaskValue(selectedConfig.tokenSecret) : maskValue(selectedConfig.tokenSecret)}
              editValue={editFormData.tokenSecret}
              editPlaceholder="Enter token secret"
              showReveal
              revealed={showTokenSecret}
              onReveal={handleToggleTokenSecretVisibility}
              onChange={(v) => setEditFormData({ ...editFormData, tokenSecret: v })}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={tokenSecretFocused}
            />
            <div className="sm:col-span-2">
              <CredentialField
                label="Restlet URL"
                hint="Optional — only needed for the netsuite_restlet AI tool (custom SuiteScript). Leave blank for all other tools."
                inputRef={restletUrlInputRef}
                viewValue={selectedConfig.restletUrl || ""}
                editValue={editFormData.restletUrl}
                editPlaceholder="https://your-account.restlets.api.netsuite.com/app/site/hosting/restlet.nl?script=...&deploy=1"
                onChange={(v) => setEditFormData({ ...editFormData, restletUrl: v })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                isFocused={restletUrlFocused}
              />
            </div>
          </div>
        </div>
      )}

      {props.configs.length === 0 && !props.onCreateMode && (
        <div className="border border-dashed border-slate-300 rounded-xl p-8 text-center">
          <p className="text-slate-500 text-sm mb-3">No NetSuite account connected yet.</p>
          <PrimaryBlueButton
            label="Connect NetSuite"
            onClick={() => navigate(`/integrations/netsuite/netsuitecreate`, { state: { teamId: props.teamId } })}
            prefixIcon={React.createElement(IconPlus)}
            className="mx-auto"
          />
        </div>
      )}

      {isModalOpen && (
        <Modal show={isModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8  text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">
                  Remove Account
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>

            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to remove the account{" "}
              {configToDelete?.displayName}? This cannot be undone and will remove all mapped fields.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-primary hover:border-primary-hover text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(configToDelete?.id)}
                className="rounded-lg px-6 py-2 bg-primary border border-primary hover:bg-primary-hover hover:border-primary-hover text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
